<template>
  <v-container fluid grid-list-md mb-2>
    <CorePageIntro>
      <template slot="content">
        This simulation depicts the impact of different sized solar arrays at
        your facility. The Production chart depicts facility load and
        co-generation output for a prior-year peak day with the simulated solar
        array production on that same day. The Week-By-Week chart depicts
        co-generation and solar power output by week (in MWh) compared to
        facility load. "Energy loss" is the curtailment of co-generation power
        output necessary to not exceed facility load, at an efficiency loss
        estimated at $0.05 per kWh.
      </template>
    </CorePageIntro>

    <v-tabs  background-color="#586776" dark>
      <!-- tab headers -->
      <v-tab>
        <v-icon class="mr-2" large>wb_sunny</v-icon>
        {{ tabTitle(0) }}
        <v-divider></v-divider>
      </v-tab>
      <v-tab>
        <v-icon class="mr-2" large>wb_sunny</v-icon>
        {{ tabTitle(1) }}
      </v-tab>
      <!-- tab content -->
      <!-- 300 ~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ -->
      <v-tab-item>
        <v-layout>
          <v-flex sm12 md4 class="text-right">            
            <CoreCardStat title="One Day: 2019-07-19">
              <template slot="subtitle"> </template>
              <template slot="stat_value">
                {{ cardDaySubtitle300 }}
              </template>
              <template slot="stat_description">
                {{ cardDayText300 }} Energy Loss
                <br />Calculated @ $0.05/kWh
              </template>
            </CoreCardStat>                
          </v-flex>

          <v-flex sm12 md4 class="text-right">
            <CoreCardStat title="One Year 2018">
              <template slot="subtitle"> </template>
              <template slot="stat_value">
                {{ cardYearSubtitle300 }}
              </template>
              <template slot="stat_description">
                {{ cardYearText300 }} Energy Loss
                <br />Calculated @ $0.05/kWh
              </template>
            </CoreCardStat>
          </v-flex>

          <v-flex sm12 md4 class="text-right">
            <CoreCardStat title="10 Years">
              <template slot="subtitle"> </template>
              <template slot="stat_value">
                {{ card10YearSubtitle300 }}
              </template>
              <template slot="stat_description">
                {{ card10YearText300 }} Energy Loss
                <br />Calculated @ $0.05/kWh
              </template>
            </CoreCardStat>
          </v-flex>
        </v-layout>

        <CoreLineChart v-if="optionsDay300" :options="optionsDay300" class="my-3"></CoreLineChart>
        <CoreLineChart v-if="optionsYear300" :options="optionsYear300" class="my-3"></CoreLineChart>

      </v-tab-item>

      <!-- 800 ~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ -->
      <v-tab-item>
        <v-layout>
          <v-flex sm12 md4 class="text-right">            
            <CoreCardStat title="One Day: 2019-07-19">
              <template slot="subtitle"> </template>
              <template slot="stat_value">
                {{ cardDaySubtitle800 }}
              </template>
              <template slot="stat_description">
                {{ cardDayText800 }} Energy Loss
                <br />Calculated @ $0.05/kWh
              </template>
            </CoreCardStat>
          </v-flex>

          <v-flex sm12 md4 class="text-right">            
            <CoreCardStat title="One Year 2018">
              <template slot="subtitle"> </template>
              <template slot="stat_value">
                {{ cardYearSubtitle800 }}
              </template>
              <template slot="stat_description">
                {{ cardYearText800 }} Energy Loss
                <br />Calculated @ $0.05/kWh
              </template>
            </CoreCardStat>
          </v-flex>

          <v-flex sm12 md4 class="text-right">            
            <CoreCardStat title="10 Years">
              <template slot="subtitle"> </template>
              <template slot="stat_value">
                {{ card10YearSubtitle800 }}
              </template>
              <template slot="stat_description">
                {{ card10YearText800 }} Energy Loss
                <br />Calculated @ $0.05/kWh
              </template>
            </CoreCardStat>
          </v-flex>
        </v-layout>

        <CoreLineChart v-if="optionsDay800" :options="optionsDay800" class="my-3"></CoreLineChart>
        <CoreLineChart v-if="optionsYear800" :options="optionsYear800" class="my-3"></CoreLineChart>

      </v-tab-item>
    </v-tabs>

  </v-container>
</template>

<script>
import MockDataService from '@/services/MockDataService'
import { mapGetters } from 'vuex'
import momentTZ from 'moment-timezone'
import COLORS from '@/util/colors'
export default {
  name: 'SimulateSolar',
  data() {
    return {
      optionsDay300: false,
      optionsYear300: false,
      optionsDay800: false,
      optionsYear800: false,
      intervals: [],
      weeks: [],
      peakDate: null,
      simulationType: null,
      energyHoursLossDay300: null,
      energyHoursLossDay800: null,
      energyHoursLossYear300: null,
      energyHoursLossYear800: null,
      energyHoursLoss10Year300: null,
      energyHoursLoss10Year800: null
    }
  },
  computed: {
    ...mapGetters(['getFacilityTimezone', 'getFacilityname']),
    /* day */
    cardDaySubtitle300() {
      if (this.energyHoursLossDay300 != null) {
        return this.$_powerMethod_kwh(this.energyHoursLossDay300)
      }
      return '-'
    },

    cardDaySubtitle800() {
      if (this.energyHoursLossDay800 != null) {
        return this.$_powerMethod_kwh(this.energyHoursLossDay800)
      }
      return '-'
    },
    cardDayText300() {
      return this.$_currencyMethod_usdNoCents(this.energyHoursLossDay300 * 0.05)
    },
    cardDayText800() {
      return this.$_currencyMethod_usdNoCents(this.energyHoursLossDay800 * 0.05)
    },
    /* year */
    cardYearSubtitle300() {
      return this.$_powerMethod_kwh(this.energyHoursLossYear300)
    },
    cardYearSubtitle800() {
      return this.$_powerMethod_kwh(this.energyHoursLossYear800)
    },
    cardYearText300() {
      return this.$_currencyMethod_usdNoCents(
        this.energyHoursLossYear300 * 0.05
      )
    },
    cardYearText800() {
      return this.$_currencyMethod_usdNoCents(
        this.energyHoursLossYear800 * 0.05
      )
    },
    /* 10 year */
    card10YearSubtitle300() {
      return this.$_powerMethod_kwh(this.energyHoursLossYear300 * 10)
    },
    card10YearSubtitle800() {
      return this.$_powerMethod_kwh(this.energyHoursLossYear800 * 10)
    },
    card10YearText300() {
      return this.$_currencyMethod_usdNoCents(
        this.energyHoursLossYear300 * 0.05 * 10
      )
    },
    card10YearText800() {
      return this.$_currencyMethod_usdNoCents(
        this.energyHoursLossYear800 * 0.05 * 10
      )
    }
  },
  mounted() {
    let json300 = MockDataService.getSimulationData(0)
    let json800 = MockDataService.getSimulationData(1)

    this.optionsDay300 = this.daily(300, json300)
    this.optionsYear300 = this.weekly(300, json300)

    this.optionsDay800 = this.daily(800, json800)
    this.optionsYear800 = this.weekly(800, json800)
  },
  methods: {

    tabTitle(idx) {
      let tabTitle = ['300kW Solar', '800kW Solar']
      return tabTitle[idx]
    },

    daily(idx, json) {
      let time_axis = []
      let load_kw = []
      let loss_kw = []
      let loss_points_kw = []
      let power_output_kw = []
      let simulated_output_kw = []
      let total_output_kw = []

      this.simulationType = json.simulation_type
      this.peakDate = json.date
      this.intervals = json.intervals

      let week = 1
      let weekly = []
      for (var i = 0; i < this.intervals.length; i++) {
        let data = this.intervals[i]
        time_axis.push(
          momentTZ(data.ending)
            .tz(this.getFacilityTimezone)
            .format('HH:mm:ss')
        )
        load_kw.push(data.load_kw)
        power_output_kw.push(data.power_output_kw)
        simulated_output_kw.push(data.simulated_output_kw)
        let total = data.power_output_kw + data.simulated_output_kw
        total_output_kw.push(total)
        if (total > data.load_kw) {
          let loss = total - data.load_kw
          loss_kw.push(loss)
          loss_points_kw.push(parseInt(total - loss))
          if (idx === 300) {
            this.energyHoursLossDay300 += parseInt(loss)
          }
          if (idx === 800) {
            this.energyHoursLossDay800 += parseInt(loss)
          }
        } else {
          loss_kw.push(null)
          loss_points_kw.push(null)
        }

        let w = {
          week: week,
          power_output_kw: data.power_output_kw * 4 * 24 * 7,
          simulated_output_kw: data.simulated_output_kw * 4 * 24 * 7,
          total_output_kw:
            (data.power_output_kw + data.simulated_output_kw) * 4 * 24 * 7,
          load_kw: data.load_kw * 4 * 24 * 7
        }
        weekly.push(w)
        week++
      }

      let title = this.peakDate + ': ' + this.tabTitle(0)
      if (idx === 800) title = this.peakDate + ': ' + this.tabTitle(1)
      let options = {
        title: {
          text: title
        },
        exporting: {
          filename:            
            'Solar_Simulation_Export'
        },
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value}'
          },
          categories: time_axis,
          crosshair: true,
        },
        yAxis: [
          {
            gridLineWidth: 1,
            labels: {
              format: '{value:,.0f} kW',
              style: {
                color: COLORS.hex.power
              }
            },
            title: {
              text: 'Target Output / Load',
              style: {
                color: COLORS.hex.power
              }
            },
          }
        ],
        series: [
          {
            type: 'area',
            name: 'Total Output (kW)',
            data: total_output_kw,
            color: COLORS.hex.power,
            // yAxis: 1,
            tooltip: {
              valueSuffix: ' kW'
            }
          },
          {
            type: 'area',
            name: this.simulationType + ' (kW)',
            data: simulated_output_kw,
            color: COLORS.hex.solar,
            yAxis: 0
          },
          {
            type: 'spline',
            name: 'Cogen Output (kW)',
            data: power_output_kw,
            color: COLORS.hex.cogen,
            // yAxis: 1,
            tooltip: {
              valueSuffix: ' kW'
            }
          },
          {
            type: 'spline',
            name: 'Energy Loss (kW)',
            data: loss_points_kw,
            color: COLORS.hex.loss,
            // yAxis: 1,
            tooltip: {
              valueSuffix: ' kW'
            }
          },
          {
            type: 'spline',
            name: 'Facility Load (kW)',
            data: load_kw,
            color: COLORS.hex.load,
            // yAxis: 1,
            tooltip: {
              valueSuffix: ' kW'
            }
          }
        ],
        tooltip: {
          shared: true,
          crosshairs: [true, true],
          backgroundColor: COLORS.hex.tooltip,
          borderColor: COLORS.hex.black,
          borderRadius: 10,
          borderWidth: 1,
          formatter: function(d) {
            var rV = '<b>Ending: ' + this.x + '</b><br/>'
            if (!d.isHidden) {
              this.points.forEach(function(d) {
                let val = 0
                if (d.series.name === 'Value') {
                  // val = parse(d.y).toFixed(2)
                  val = '$' + d.y.toLocaleString()
                } else {
                  val = parseInt(d.y).toLocaleString()
                  val += ' kW'
                }
                rV +=
                  '<span style="color:' +
                  d.color +
                  '">\u25CF</span> ' +
                  d.series.name +
                  ': <b> ' +
                  val +
                  '</b><br/>'
              })
            }
            return rV
          }
        }
      }
      return options
    },

    weekly(idx, json) {
      let week = []
      let power_output_kw = []
      let simulated_output_kw = []
      let total_output_kw = []
      let load_kw = []

      this.simulationType = json.simulation_type
      this.peakDate = json.date
      this.weeks = json.weekly

      for (var i = 0; i < this.weeks.length; i++) {
        let data = this.weeks[i]
        week.push(data.week)
        power_output_kw.push(parseInt(data.power_output_kw) / 1000)
        simulated_output_kw.push(parseInt(data.simulated_output_kw) / 1000)
        total_output_kw.push(
          parseInt(data.power_output_kw + data.simulated_output_kw) / 1000
        )
        load_kw.push(parseInt(data.load_kw) / 1000)

        // do not convert to MW, leave as kWh
        let total = data.power_output_kw + data.simulated_output_kw
        let loss = total - data.load_kw
        if (total > data.load_kw) {
          if (idx === 300) {
            this.energyHoursLossYear300 += parseInt(loss)
          }
          if (idx === 800) {
            this.energyHoursLossYear800 += parseInt(loss)
          }
        }
      }

      let title = '2018 Weekly: ' + this.tabTitle(0)
      if (idx === 800) title = '2018 Weekly: ' + this.tabTitle(1)
      let options = {
        title: {
          text: title
        },
        exporting: {
          filename:            
            'Solar_Simulation_Weekly_Export'
        },
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value}'
          },
          categories: week,
          crosshair: true,
        },
        yAxis: [
          {
            gridLineWidth: 1,
            labels: {
              format: 'Output (MWh)',
              style: {
                color: COLORS.hex.power
              }
            },
            title: {
              text: 'Target Output / Load',
              style: {
                color: COLORS.hex.power
              }
            },
          }
        ],
        series: [
          {
            type: 'column',
            name: 'Total Output (kW)',
            data: total_output_kw,
            color: COLORS.hex.power,
            // yAxis: 1,
            tooltip: {
              valueSuffix: ' MWh'
            }
          },
          {
            type: 'area',
            name: this.simulationType + ' (MWh)',
            data: simulated_output_kw,
            color: COLORS.hex.solar,
            yAxis: 0
          },
          {
            type: 'spline',
            name: 'Cogen  (MWh)',
            data: power_output_kw,
            color: COLORS.hex.cogen,
            // yAxis: 1,
            tooltip: {
              valueSuffix: ' MWh'
            }
          },
          {
            type: 'spline',
            name: 'Facility Load (MWh)',
            data: load_kw,
            color: COLORS.hex.load,
            // yAxis: 1,
            tooltip: {
              valueSuffix: ' MWh'
            }
          },
          {
            type: 'spline',
            name: 'Facility Load (kW)',
            data: total_output_kw,
            color: COLORS.hex.load,
            // yAxis: 1,
            tooltip: {
              valueSuffix: ' kW'
            }
          }

        ],
        tooltip: {
          shared: true,
          crosshairs: [true, true],
          backgroundColor: COLORS.hex.tooltip,
          borderColor: COLORS.hex.black,
          borderRadius: 10,
          borderWidth: 1,
          formatter: function(d) {
            var rV = '<b>Ending: ' + this.x + '</b><br/>'
            if (!d.isHidden) {
              this.points.forEach(function(d) {
                let val = 0
                if (d.series.name === 'Value') {
                  // val = parse(d.y).toFixed(2)
                  val = '$' + d.y.toLocaleString()
                } else {
                  val = parseInt(d.y).toLocaleString()
                  val += ' kW'
                }
                rV +=
                  '<span style="color:' +
                  d.color +
                  '">\u25CF</span> ' +
                  d.series.name +
                  ': <b> ' +
                  val +
                  '</b><br/>'
              })
            }
            return rV
          }
        }
      }
      return options
    }
  }
}
</script>
